import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface LowBlockUptakeEntry {
  personId: string | null
  firstName: string | null
  surname: string | null
  location: string[] | null
  sheriffType: string | null
  filledShiftCount: number
  driverCancelledCount: number
  totalShifts: number
}

export interface LowBlockUptakeResponse {
  success: boolean
  lowBlockUptake: LowBlockUptakeEntry[]
  error?: string
}

export interface LowBlockUptakeRequest {
  startDate: string
  endDate: string
  location?: string
  sheriffTypes?: readonly string[]
  maxFilledShifts?: number
}

export function useLowBlockUptake(request: LowBlockUptakeRequest) {
  return useQuery<LowBlockUptakeResponse>({
    queryKey: ["low-block-uptake", request],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const functionUrl = `${getFunctionsBaseUrl()}/low-block-uptake`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error || `Failed to fetch low block uptake: ${response.statusText}`,
        )
      }

      const data: LowBlockUptakeResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch low block uptake")
      }

      return data
    },
    enabled: !!request.startDate && !!request.endDate,
    staleTime: 30 * 1000,
  })
}
